/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import clsx from 'clsx';

import { SurveyTypes } from '../features/sharedcomponents/Login/types';
import { postSurveyAcceptance } from '../features/sharedcomponents/utils';
import styles from './SurveyRenderer.module.scss';
import { analyticsTrack } from '../features/sharedcomponents/analytics_utils';
import { useAppSelector } from '../app/hooks';
import { NPSQuestions, selectPearlUserInfo } from '../features/sharedcomponents/Login/userSlice';
import pearlApi from '../features/sharedcomponents/api/pearlApi';

function LegalDisclaimerSurvey({
  surveyType,
  show,
  closeDisclaimer,
}: {
  surveyType?: SurveyTypes;
  show: boolean;
  closeDisclaimer: (show: boolean) => void;
}) {
  return surveyType === SurveyTypes.legalDisclaimer ? (
    <Dialog
      open={show}
      fullScreen={false}
      PaperProps={{
        className: styles.dialogBox,
      }}
      disableEscapeKeyDown
      id="disclaimer_root"
    >
      <LegalDisclaimerContent closeDisclaimer={closeDisclaimer} />
    </Dialog>
  ) : null;
}
export default LegalDisclaimerSurvey;
export const LegalDisclaimerContent = ({ closeDisclaimer }: { closeDisclaimer: (show: boolean) => void }) => {
  const survey = { name: SurveyTypes.legalDisclaimer };
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  return (
    <div className={styles.container}>
      <b>
        <h4 className={styles.heading}>MedPearl Legal Disclaimer and Notice</h4>
      </b>
      <div className={styles.content}>
        <span>
          MedPearl and its proprietary programs (e.g. Clinical Knowledge Hub, Pearl, Actionable Intelligence, MedPearl
          Patient) is designed to consolidate clinical content and information into consumable quantities.&nbsp; The
          materials contained herein are for informational purposes only and are not meant as a substitute for a
          provider’s clinical judgement.&nbsp; Further, unless otherwise specified, all content that is provided, upon
          submission, becomes property of Providence Health &amp; Services – Washington (“Providence”).
        </span>
        <br></br>
        <span>
          <b>Patient Information Display Limitations and Representations</b>. Where MedPearl presents certain medical
          information attributable to a particular patient, such information is limited to that which is available
          within the Customer’s electronic health record and is not guaranteed to be complete, contemporaneous, or
          correct. Any information displayed is not stored in nor maintained in the MedPearl platform nor does it
          constitute a medical record for retention purposes.
        </span>
        <br></br>
        <span>
          <b>Independent Medical Practice Expectations.&nbsp; </b>MedPearl is not a substitute for medical examination
          and should only be used in conjunction with a full review of the patient’s condition and medical
          history/record by a medical professional. MedPearl does not provide medical advice and should not be used as a
          sole source for diagnosis or treatment of any condition.&nbsp; These topics should not be construed as
          dictating an exclusive course of treatment.&nbsp; Variations in practice may be warranted based on the needs
          of the individual patient, resources, and limitations unique to the institutional type of practice including
          the source author’s variation. &nbsp;Use of MedPearl shall neither create a physician-patient relationship nor
          constitute the practice of medicine. Providence is neither responsible for any omissions, defects or failures
          associated with MedPearl content nor any damages that may result from any such defects or
          failures.&nbsp;Neither MedPearl nor Providence recommends or endorses any specific tests, physicians,
          products, procedures, opinions, or other information that may be mentioned on MedPearl or in a link on
          MedPearl.
        </span>
        <br></br>
        <span>
          <b>No medical advice</b>.&nbsp; MedPearl is not a health care provider and MedPearl does not operate as
          Software as a Medical Device.&nbsp; The content is not intended to provide medical advice, diagnosis, or
          treatment or substitute for an individual patient assessment.&nbsp; The content is for general medical
          education only.
        </span>
        <br></br>
        <span>
          <b>Liability Limitations for Content Authorship.&nbsp; </b>Every effort is made by contributing authors and
          editors to ensure that information in these topics is accurate and up to date.&nbsp; Neither Providence nor
          any contributing authors or editors, accepts liability for any damages occurring from any errors or omissions
          in any information found in this platform.&nbsp; The final responsibility for a patient’s care lies with the
          Customer and its medical provider.
        </span>
        <br></br>
        <span>
          <b>Limitations on Brand Inclusion Representations</b>.&nbsp; The inclusion of brand names of certain
          prescriptions or over the counter medications as examples in some MedPearl content does not constitute
          endorsement by Providence. MedPearl content may provide links to other sites on the Internet for your
          convenience. These other sites are maintained by third parties over which Providence exercises no control. The
          appearance of any such third-party links is not intended to endorse any particular company or product. If you
          decide to access any of the third-party sites linked to from the MedPearl platform, you do so entirely at your
          own risk. &nbsp;Any inclusion of medications, dosing, routing, or other specifications should be verified by
          the provider prior to prescription or use.&nbsp; Some topics contain discussions of ‘off-label’ drug use that
          is not approved by the Federal Drug Administration and should be considered independently by the treating
          provider.
        </span>
        <br></br>
        <span>
          <b>Content Disclaimer</b>.&nbsp; MedPearl provides the licensed content to you on an “as-is” basis.&nbsp;
          MedPearl does not make any warrant of any kind, express or implied. &nbsp;MedPearl specifically disclaims any
          implied warrants of fitness for a particular purpose, merchantability, particular purpose, non-infringement,
          accuracy, quality correctness, completeness, comprehensiveness, suitability, system availability, and
          compatibility.&nbsp; This disclaimer is subject to the laws of any state that do not allow disclaimers of
          implied warranties.&nbsp; You acknowledge that no representations have been made respecting the licensed
          MedPearl content.&nbsp;
        </span>
        <br></br>
        <span>
          <b>Technical Disclaimer</b>. You acknowledge that the licensed content and any information contained in the
          licensed content or any communication provided to MedPearl during use of the licensed content will be
          transmitted over a medium that is beyond the control of MedPearl and may be subject to limitations, delays,
          latency issues, and other problems inherent in the use of the internet and electronic communications, and
          MedPearl is not responsible for and assumes no liability for any delays, delivery failures, corruption of any
          data or other damage resulting from such problems.&nbsp; MedPearl is not responsible for and assumes no
          liability for any technical malfunction, inability to access the licensed content or other problems related to
          or arising from any third party hardware or software, including any third party website, application, or other
          platform.
        </span>
        <br></br>
        <span>
          <b>Consumer Information Collection and Opt Out Policy</b>.&nbsp; The MedPearl platform neither collects
          patient information nor stores it.&nbsp; The platform may track users of the content to evaluate program
          efficacy, adjust clinical topics, and enhance performance.&nbsp; There is no intent to sell, advertise, or
          otherwise use the information collected, collectively referred to as consumer personal information, at this
          time.&nbsp; An individual who wishes to opt-out of their data collection may request to do so by emailing{' '}
          <a
            style={{ textDecoration: 'underline', color: '#467886' }}
            href="mailto:medpearlsupport@healthendeavors.com"
          >
            medpearlsupport@healthendeavors.com
          </a>{' '}
          and providing written notification of their desire to not have any personal information collected about
          them.&nbsp; A copy of their current data profile may be obtained on request at the above email address.&nbsp;
          The data may still be collected and anonymized for program improvement.
        </span>
        <br></br>
        <span>
          <b>Feedback Ownership</b>.&nbsp; You agree and acknowledge that any feedback provided on a clinical topic or
          in general may be used by MedPearl without restriction and for any purpose, including, but not limited to
          creating derivative works based upon that information.&nbsp; Feedback means your suggestions, comments,
          corrections, discoveries, ideas, improvements, insights, opinions, preferences, suggestions, or any other
          feedback provided by you to MedPearl in connection with your use.
        </span>
        <br></br>
        <b>
          <span>Copyright and Permission to reproduce material.</span>
        </b>
        <span>
          &nbsp; All content in MedPearl is created by humans and subject to the copyright and protections associated
          with that work.&nbsp; Permission to reproduce material from MedPearl in another publication must be secured in
          writing with the express written consent of the company for external use.
        </span>
      </div>
      <Button
        variant="contained"
        className={styles.submitButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--blue)';
        }}
        onClick={() => {
          void postSurveyAcceptance(survey);
          closeDisclaimer(false);
          analyticsTrack('legal_disclaimer_consumer_accepted', {}, pearlUserInfo);
        }}
      >
        Okay, got it!
      </Button>
    </div>
  );
};
type NPSQuestion = Pick<NPSQuestions, 'id' | 'position' | 'title' | 'question_type' | 'description'>;

const DEFAULT_COMMENT_DESCRIPTION = "Please share what's working well, what could be improved, or any other feedback.";

export const NPSSurvey = ({
  show,
  closeNps,
  questions = [],
  npsSurveyId,
}: {
  show: boolean;
  closeNps: (state: boolean) => void;
  questions?: NPSQuestion[];
  npsSurveyId?: string;
}) => {
  const [position, setPosition] = useState(1);
  // Maintain a record of responses in case we have multiple questions
  const [responses, setResponses] = useState<Record<string, { rating?: number; comment?: string }>>({});
  // Rating choices from 1-10
  const choices = Array.from({ length: 11 }, (_, i) => i);
  const currentQuestion = questions.find((q) => q.position === position);

  const handleRatingChange = (questionId: string, value: number) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], rating: value },
    }));
  };

  const handleCommentChange = (questionId: string, value: string) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], comment: value },
    }));
  };

  const handleSurveySubmit = () => {
    const formattedResponses = Object.entries(responses).map(([question_id, response]) => ({
      question_id,
      ...response,
    }));
    pearlApi
      .post('/surveys/nps', formattedResponses)
      .then(() => {
        console.log('Survey submitted successfully');
        closeNps(false);
      })
      .catch((error) => {
        console.error('Error submitting survey:', error);
      });
  };

  async function handleNPSClose() {
    try {
      await pearlApi.post(`/prompts/${npsSurveyId}/dismiss`);
    } catch (ex) {
      console.error(`Something went wrong with closing the NPS survey:${(ex as Error).message}`);
    } finally {
      closeNps(false);
    }
  }
  return (
    <Dialog
      open={show}
      hideBackdrop
      disableAutoFocus
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '90vw',
          padding: '10px 20px',
          margin: 'auto',
          border: '1px solid var(--darkblue)',
          minWidth: 'min(100%, 1000px)',
          minHeight: '265px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
      data-testid="nps-dialog"
    >
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <IconButton
            aria-label="close"
            onClick={handleNPSClose}
            data-testid="nps-close-button"
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {currentQuestion && (
          <div
            className={
              currentQuestion.question_type === 'rating_with_comment' ? styles.flexColumn : styles.alignedColumn
            }
            key={currentQuestion.id}
          >
            {/* Back Button (Only Show if Not on First Question) */}
            {position > 1 && (
              <Button onClick={() => setPosition(position - 1)} className={styles.backBtn} data-testid="nps-backBtn">
                ❮ Back
              </Button>
            )}

            <p
              style={{ fontWeight: 500, marginTop: '10px', fontSize: '24px', textAlign: 'center' }}
              data-testid={`nps-question-${position}`}
            >
              {currentQuestion.title}
            </p>

            {currentQuestion.question_type === 'rating_with_comment' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  flexWrap="wrap"
                  data-testid="nps-chip-group"
                  alignItems="center"
                >
                  <span style={{ color: 'var(--npi-input-focus)', fontSize: 12, fontStyle: 'italic' }}>
                    Not at all likely
                  </span>
                  {choices.map((num) => (
                    <div key={num} className={clsx(styles.flexColumn, styles.flexSpan)}>
                      <Chip
                        label={num}
                        onClick={() => handleRatingChange(currentQuestion.id, num)}
                        data-testid={`nps-rating-${num}`}
                        sx={{
                          backgroundColor:
                            responses[currentQuestion.id]?.rating === num
                              ? 'var(--darknavyblue)'
                              : 'var(--default-chip)',
                          color: responses[currentQuestion.id]?.rating === num ? '#fff' : 'inherit',
                          borderRadius: '5px',
                          height: { xs: 40, md: 50 },
                          width: { xs: 40, md: 50 },
                          cursor: 'pointer',
                        }}
                      />
                    </div>
                  ))}
                  <span style={{ color: 'var(--npi-input-focus)', fontSize: 12, fontStyle: 'italic' }}>
                    Extremely likely
                  </span>
                </Stack>
                <FormLabel>Tell us more about your rating</FormLabel>
                <p className={styles.commentDescription} data-testid="nps-comment-description">
                  {currentQuestion.description ?? DEFAULT_COMMENT_DESCRIPTION}
                </p>
                <TextareaAutosize
                  minRows={3}
                  maxRows={3}
                  value={responses[currentQuestion.id]?.comment ?? ''}
                  onChange={(e) => handleCommentChange(currentQuestion.id, e.target.value)}
                  style={{
                    fontFamily: 'unset',
                    width: '90%',
                    padding: '8px',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    maxWidth: '90%',
                  }}
                  data-testid="nps-comment"
                />
              </div>
            )}

            {currentQuestion.question_type === 'comment' && (
              <TextareaAutosize
                minRows={4}
                maxRows={4}
                placeholder="Please explain"
                value={responses[currentQuestion.id]?.comment ?? ''}
                onChange={(e) => handleCommentChange(currentQuestion.id, e.target.value)}
                style={{
                  fontFamily: 'unset',
                  width: '90%',
                  padding: '8px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  maxWidth: '90%',
                }}
                data-testid="nps-comment"
              />
            )}

            {/* Navigation Buttons */}
            <div className={styles.btnDiv}>
              {position < questions.length ? (
                <Button
                  disabled={
                    currentQuestion.question_type === 'rating_with_comment' &&
                    responses[currentQuestion.id]?.rating == null
                  }
                  className={styles.nextButton}
                  variant="outlined"
                  onClick={() => setPosition(position + 1)}
                  data-testid="nps-next"
                >
                  Next ❯
                </Button>
              ) : (
                <Button
                  onClick={handleSurveySubmit}
                  variant="contained"
                  className={styles.submitButton}
                  data-type="nps"
                  disabled={questions.some(
                    (q) => q.question_type === 'rating_with_comment' && responses[q.id]?.rating == null
                  )}
                  data-testid="nps-submit"
                >
                  Submit
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
